import React, { useEffect, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import socioService from "../services/socioService.tsx";
import { asociarSocioAServicio, ServicioAsignacion } from "../services/serviciosAsociacionService.ts";
import { Socio } from "../types/Socio.tsx";

type ServicioInfo = {
  id: number;
  nombre: string;
  categoria?: string;
  descripcion?: string;
};

export default function AsignarServicioModal({
  servicio,
  onClose,
  onAsignado,
  sociosAsignados = [],
  loadingSociosAsignados = false,
}: {
  servicio: ServicioInfo;
  onClose: () => void;
  onAsignado?: (socio: { socio_id?: string | number; socio_nombre?: string }) => void;
  sociosAsignados?: ServicioAsignacion[];
  loadingSociosAsignados?: boolean;
}) {
  const { getAccessTokenSilently } = useAuth0();
  const [searchTerm, setSearchTerm] = useState("");
  const [listaSocios, setListaSocios] = useState<Socio[]>([]);
  const [pagina, setPagina] = useState(1);
  const porPagina = 3;
  const [searching, setSearching] = useState(false);
  const [assigningId, setAssigningId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [exito, setExito] = useState<string | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [isClosing, setIsClosing] = useState(false);

  useEffect(() => {
    const id = requestAnimationFrame(() => setIsVisible(true));
    return () => cancelAnimationFrame(id);
  }, []);

  const closeModal = () => {
    if (isClosing) return;
    setIsClosing(true);
    setIsVisible(false);
    setTimeout(() => onClose(), 180);
  };

  const handleSearch = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    setExito(null);
    setListaSocios([]);
    setPagina(1);

    if (!searchTerm.trim()) {
      setError("Ingresá un texto para buscar.");
      return;
    }
    setSearching(true);
    try {
      const data = await socioService.searchSocios(searchTerm.trim(), getAccessTokenSilently);
      if (!data?.length) {
        setError("Sin resultados para esa búsqueda.");
      } else {
        setListaSocios(data);
      }
    } catch (err) {
      console.error("Error al buscar socios", err);
      setError("No se pudieron cargar los socios.");
    } finally {
      setSearching(false);
    }
  };

  const handleAssign = async (socio: Socio) => {
    setAssigningId(String(socio.socio_id));
    setError(null);
    setExito(null);
    try {
      const token = await getAccessTokenSilently();
      await asociarSocioAServicio(
        {
          socioId: Number(socio.socio_id),
          servicioId: Number(servicio.id),
          contacto: {
            nombre: socio.socio_nombre,
            email: socio.socio_mail,
            telefono: socio.socio_celular || socio.socio_telefono,
          },
        },
        token
      );
      setExito(
        `Servicio "${servicio.nombre}" asignado a ${socio.socio_nombre || socio.socio_id}.`
      );
      closeModal();
      setTimeout(() => {
        onAsignado?.({
          socio_id: socio.socio_id,
          socio_nombre: socio.socio_nombre,
        });
      }, 190);
    } catch (err: any) {
      console.error("Error al asignar servicio", err);
      const apiMsg = err?.response?.data?.message || err?.message || "No se pudo asignar el servicio.";
      setError(apiMsg);
    } finally {
      setAssigningId(null);
    }
  };

  const totalPaginas = Math.max(1, Math.ceil(listaSocios.length / porPagina));
  const paginaActual = Math.min(pagina, totalPaginas);
  const desde = (paginaActual - 1) * porPagina;
  const resultadosPagina = listaSocios.slice(desde, desde + porPagina);

  return (
    <div
      className={`fixed inset-0 z-50 flex items-center justify-center px-4 py-8 backdrop-blur transition-all duration-200 ${
        isVisible ? "bg-gray-900/60" : "bg-gray-900/0"
      }`}
    >
      <div
        className={`w-full max-w-5xl rounded-2xl bg-white p-6 shadow-2xl transition-all duration-200 ${
          isVisible ? "translate-y-0 scale-100 opacity-100" : "translate-y-2 scale-[0.985] opacity-0"
        }`}
      >
        <div className="grid gap-6 lg:grid-cols-[1fr,1.1fr]">
          <div className="space-y-3">
            <h2 className="text-xl font-bold text-gray-900">Asignar servicio</h2>
            <p className="text-sm text-gray-600">
              {servicio.categoria ? `${servicio.categoria} · ` : ""}
              {servicio.nombre}
            </p>
            {servicio.descripcion && (
              <p className="text-sm text-gray-600">{servicio.descripcion}</p>
            )}
            {(loadingSociosAsignados || sociosAsignados.length > 0) && (
              <div className="space-y-2 rounded-xl bg-gray-50 p-3">
                <p className="text-sm font-semibold text-gray-800">Socios asignados a este servicio</p>
                {loadingSociosAsignados ? (
                  <div className="space-y-1">
                    {Array.from({ length: 3 }).map((_, idx) => (
                      <div
                        key={`sk-asignado-${idx}`}
                        className="flex items-center justify-between rounded-lg bg-white px-3 py-2 shadow-sm animate-pulse"
                      >
                        <div className="flex w-full flex-col gap-2">
                          <div className="h-3 w-36 rounded bg-slate-200" />
                          <div className="h-3 w-52 rounded bg-slate-200" />
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="space-y-1">
                    {sociosAsignados.map((s, idx) => (
                      <div
                        key={`${s.socio_id}-${idx}`}
                        className="flex items-center justify-between rounded-lg bg-white px-3 py-2 text-xs text-gray-700 shadow-sm"
                      >
                        <div className="flex flex-col">
                          <span className="font-semibold text-gray-900">
                            {s.socio_nombre || s.socio_id || "Socio"}
                          </span>
                          <span className="text-[11px] text-gray-500">
                            {s.socio_mail || s.socio_celular || "Sin contacto"}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
            {error && <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>}
            {exito && <p className="rounded-lg bg-emerald-50 px-3 py-2 text-sm text-emerald-700">{exito}</p>}
            <button
              onClick={closeModal}
              disabled={isClosing}
              className="w-full rounded-lg px-4 py-2 text-sm font-semibold text-gray-600 transition hover:bg-gray-100 disabled:opacity-60"
            >
              Cerrar
            </button>
          </div>

          <div className="space-y-3">
            <form onSubmit={handleSearch} className="space-y-2 rounded-xl border border-gray-100 bg-gray-50/80 p-3">
              <p className="text-sm font-semibold text-gray-800">Buscar socio</p>
              <input
                type="text"
                placeholder="Por nombre o ID"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full rounded-lg border border-gray-200 px-3 py-2.5 shadow-inner focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100"
              />
              <button
                type="submit"
                disabled={searching}
                className="w-full rounded-lg bg-blue-600 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-blue-300"
              >
                {searching ? "Buscando..." : "Buscar"}
              </button>
            </form>

            {searching && (
              <div className="space-y-2 rounded-xl border border-gray-100 bg-white p-3">
                <p className="text-sm font-semibold text-gray-800">Buscando socios...</p>
                {Array.from({ length: porPagina }).map((_, idx) => (
                  <div
                    key={`sk-socio-${idx}`}
                    className="flex items-center justify-between rounded-lg border border-gray-100 px-3 py-2 shadow-sm animate-pulse"
                  >
                    <div className="flex w-full flex-col gap-2">
                      <div className="h-4 w-40 rounded bg-slate-200" />
                      <div className="h-3 w-56 rounded bg-slate-200" />
                    </div>
                    <div className="h-7 w-16 rounded bg-slate-200" />
                  </div>
                ))}
              </div>
            )}

            {!searching && listaSocios.length > 0 && (
              <div className="space-y-2 rounded-xl border border-gray-100 bg-white p-3">
                <p className="text-sm font-semibold text-gray-800">
                  Resultados ({listaSocios.length})
                </p>
                {resultadosPagina.map((s) => (
                  <div
                    key={s.socio_id}
                    className="flex items-center justify-between rounded-lg border border-gray-100 px-3 py-2 text-sm text-gray-700 shadow-sm"
                  >
                    <div className="flex flex-col">
                      <span className="font-semibold text-gray-900">{s.socio_nombre || s.socio_id}</span>
                      <span className="text-xs text-gray-500">
                        {s.socio_mail || s.socio_celular || s.socio_telefono || "Sin contacto"}
                      </span>
                    </div>
                    <button
                      onClick={() => handleAssign(s)}
                      disabled={assigningId === String(s.socio_id)}
                      className="rounded-lg bg-emerald-600 px-3 py-1 text-xs font-semibold text-white shadow-sm transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:bg-emerald-300"
                    >
                      {assigningId === String(s.socio_id) ? "Asignando..." : "Asignar"}
                    </button>
                  </div>
                ))}

                {listaSocios.length > porPagina && (
                  <div className="flex items-center justify-between border-t pt-2 text-xs text-slate-600">
                    <span>Página {paginaActual} de {totalPaginas}</span>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => setPagina((p) => Math.max(1, p - 1))}
                        disabled={paginaActual === 1}
                        className="rounded border px-2 py-1 disabled:opacity-40"
                      >
                        Anterior
                      </button>
                      <button
                        type="button"
                        onClick={() => setPagina((p) => Math.min(totalPaginas, p + 1))}
                        disabled={paginaActual === totalPaginas}
                        className="rounded border px-2 py-1 disabled:opacity-40"
                      >
                        Siguiente
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
